import {
  CalculateOrder,
  CreateOrderResponse,
  FullOrderItem,
  GetOrderDraftResponse,
  ICreateOrder,
  IFullOrder,
  InitOrder,
  INotYetOrdered,
  IReturnOrderListResponse,
  ISpecialOffersOrders,
  IStatisticsClientOrders,
  OrderCalculation,
  OrderLine,
  OrderProductResponse,
  OrderReturnProductsResponse,
  ReturnItem,
  StatisticsClientOrdersResponse,
  UpdateOrderProduct,
} from './../models/IOrder/IOrder';
import { RootState } from './../redux/store';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_BASE_URL } from '../helpers/common';

import { IOrder, IOrderFilter, IOrderListResponse } from '../interfaces/IOrders';
import { IProductResponse, IProductFilter } from '../interfaces/IProduct';

export interface IInvalidAttributeOrders {
  count: number;
  list: Array<{
    id: number;
    code: string | null;
    stockOwnerId: number | null;
  }>;
}

export const ordersApi = createApi({
  reducerPath: 'ordersApi',
  tagTypes: ['Order'],
  refetchOnMountOrArgChange: true,
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: async (headers, { getState }) => {
      const token = (getState() as RootState).user.info.accessToken;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (build) => ({
    getOrders: build.query<
      IOrderListResponse,
      {
        search: string;
        page: number;
        from_date: string;
        to_date: string;
        area_id: string;
        agent_id: string;
        status_id: string;
        days: number[] | undefined;
      }
    >({
      transformResponse: (response: { data: IOrderListResponse }) =>
        response.data,
      query: ({
        search,
        page = 1,
        from_date,
        to_date,
        area_id,
        agent_id,
        status_id,
        days,
      }) => ({
        url: '/orders',
        method: 'GET',
        params: {
          search,
          page,
          from_date,
          to_date,
          area_id,
          agent_id,
          status_id,
          days: JSON.stringify(days),
        },
      }),
      providesTags: (result) => ['Order'],
    }),
    getOrdersList: build.query<IOrderListResponse, IOrderFilter>({
      transformResponse: (response: { data: IOrderListResponse }) =>
        response.data,
      query: (params: IOrderFilter) => ({
        url: '/orders/list',
        method: 'GET',
        params,
      }),
      providesTags: () => ['Order'],
    }),
    getInvalidAttributeOrders: build.query<IInvalidAttributeOrders, void>({
      transformResponse: (response: { data: IInvalidAttributeOrders }) =>
        response.data,
      query: () => ({
        url: '/orders/invalid-attributes',
        method: 'GET',
      }),
      providesTags: () => ['Order'],
    }),
    getSelectableProducts: build.query<IProductResponse, IProductFilter>({
      transformResponse: (response: { data: IProductResponse }) => response.data,
      query: (params: IProductFilter) => ({
        url: '/orders/selectable-products',
        method: 'GET',
        params,
      }),
    }),
    getOrdersCounters: build.query<any, void>({
      transformResponse: (response: { data: { counter: number } }) => 
        response.data,
      query: () => ({
        url: '/orders/new-counter',
        method: 'GET',
      }),
    }),
    readOrdersCounters: build.query<any, string>({
      query: (key) => ({
        url: `/orders/new-counter/${key}`,
        method: 'GET',
      }),
    }),
    getOrderProducts: build.query<
      OrderProductResponse,
      {
        categoryId: number | null;
        page?: number;
        search: string;
        business_id: string;
      }
    >({
      transformResponse: (response: { data: OrderProductResponse }) =>
        response.data,
      query: ({ categoryId, page = 1, search, business_id }) => ({
        url: `/orders/products`,
        method: 'GET',
        params: {
          category_id: categoryId || '',
          search,
          business_id,
        },
      }),
    }),
    getOrderLines: build.query<OrderLine[], string>({
      transformResponse: (response: { data: OrderLine[] }) => response.data,
      query: () => ({
        url: `/orders/lines`,
        method: 'GET',
      }),
    }),
    statisticsClientOrders: build.query<
      StatisticsClientOrdersResponse,
      { id: string; from_date: string; to_date: string; page: number }
    >({
      transformResponse: (response: { data: StatisticsClientOrdersResponse }) =>
        response.data,
      query: ({ id, from_date, to_date, page }) => ({
        url: `/agents/${id}/statistics-client-orders`,
        method: 'GET',
        params: { from_date, to_date, page },
      }),
    }),
    initOrder: build.query<InitOrder, string>({
      transformResponse: (response: { data: InitOrder }) => response.data,
      query: (business_id) => ({
        url: `/orders/init`,
        method: 'GET',
        params: {
          business_id,
        },
      }),
    }),
    getOrder: build.query<IOrder, string>({
      transformResponse: (response: { data: IOrder }) => response.data,
      query: (orderId) => ({
        url: `/orders/${orderId}`,
        method: 'GET',
      }),
    }),
    calculateOrderPrice: build.mutation<OrderCalculation, CalculateOrder>({
      transformResponse: (response: { data: OrderCalculation }) =>
        response.data,
      query: (productData) => ({
        url: '/orders/calculation',
        method: 'POST',
        body: productData,
      }),
    }),
    createOrder: build.mutation<CreateOrderResponse, ICreateOrder>({
      transformResponse: (response: { data: CreateOrderResponse }) =>
        response.data,
      query: (orderData) => ({
        url: '/orders/create',
        method: 'POST',
        body: orderData,
      }),
    }),
    /** AI-prepared draft prefill for `/order/add?draftId=...` — always
     * re-fetched from the DB, never trusts anything the AI said in chat. */
    getOrderDraft: build.query<GetOrderDraftResponse, number>({
      transformResponse: (response: { data: GetOrderDraftResponse }) =>
        response.data,
      query: (draftId) => ({
        url: `/orders/drafts/${draftId}`,
        method: 'GET',
      }),
    }),
    updateOrder: build.mutation<
      CreateOrderResponse,
      {
        orderId: string;
        data: {
          shipping?: {
            address?: string;
            stateId?: number | null;
            cityId?: number | null;
            phone?: string;
            contact?: string;
            notes?: string;
            deliveryDays?: number;
            deliveryAt?: string;
            isSelfPickUp?: boolean;
          };
          products?: { id: number; quantity: number }[];
          palletes?: { palleteId: number; quantity: number; warehouseId?: number; qtyPieces?: number }[];
          attachmentSessionId?: number;
          attributeValues?: Array<{ definitionId: number; value: unknown }>;
        };
      }
    >({
      transformResponse: (response: { data: CreateOrderResponse }) =>
        response.data,
      query: ({ data, orderId }) => ({
        url: `/orders/${orderId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Order'],
    }),
    getRecentShippingAddresses: build.query<
      Array<{
        address: string;
        cityId: number;
        cityName: string;
        cityNameHe: string;
        stateId?: number;
        phone?: string;
        contact?: string;
        label: string;
      }>,
      { clientId?: number; stockOwnerId?: number; limit?: number }
    >({
      transformResponse: (response: { data: any[] }) => response.data,
      query: ({ clientId, stockOwnerId, limit }) => ({
        url: '/orders/shipping/recent-addresses',
        method: 'GET',
        params: {
          clientId,
          stockOwnerId,
          limit: limit || 10,
        },
      }),
    }),
    changeOrderStatus: build.mutation<
      any,
      { orderId: string; reject?: string; status_id: number }
    >({
      query: ({ reject, orderId, status_id }) => ({
        url: `/orders/${orderId}/change-status`,
        method: 'POST',
        body: {
          reject,
          status_id,
        },
      }),
    }),
    getPreviousProducts: build.mutation<FullOrderItem[], string>({
      transformResponse: (response: { data: FullOrderItem[] }) => response.data,
      query: (business_id) => ({
        url: '/orders/previous-product',
        method: 'GET',
        params: {
          business_id,
        },
      }),
    }),
    getOrdersHistory: build.query<
      IOrderListResponse,
      {
        search: string;
        page: number;
        from_date: string;
        to_date: string;
        area_id: string;
        agent_id: string;
        status_id: string;
        days: number[] | undefined;
      }
    >({
      transformResponse: (response: { data: IOrderListResponse }) =>
        response.data,
      query: ({
        search,
        page = 1,
        from_date,
        to_date,
        area_id,
        agent_id,
        status_id,
        days,
      }) => ({
        url: '/orders/history',
        method: 'GET',
        params: {
          search,
          page,
          from_date,
          to_date,
          area_id,
          agent_id,
          status_id,
          days: JSON.stringify(days),
        },
      }),
    }),
    getFutureOrders: build.query<
      IOrderListResponse,
      {
        search: string;
        page: number;
        from_date: string;
        to_date: string;
        area_id: string;
        agent_id: string;
        status_id: string;
        days: number[] | undefined;
      }
    >({
      transformResponse: (response: { data: IOrderListResponse }) =>
        response.data,
      query: ({
        search,
        page = 1,
        from_date,
        to_date,
        area_id,
        agent_id,
        status_id,
        days,
      }) => ({
        url: '/orders/future',
        method: 'GET',
        params: {
          search,
          page,
          from_date,
          to_date,
          area_id,
          agent_id,
          status_id,
          days: JSON.stringify(days),
        },
      }),
    }),
    createProductsReturns: build.mutation<
      OrderReturnProductsResponse,
      { orderId: string; data: UpdateOrderProduct }
    >({
      query: ({ orderId, data }) => ({
        url: `/orders/${orderId}/return-items`,
        method: 'POST',
        body: data,
      }),
    }),
    updateOrderProducts: build.mutation<
      any,
      { orderId: string; data: UpdateOrderProduct }
    >({
      query: ({ orderId, data }) => ({
        url: `/orders/${orderId}/change-products`,
        method: 'POST',
        body: data,
      }),
    }),
    notYetOrdered: build.query<
      INotYetOrdered[],
      {
        search: string;

        from_date: string;
        to_date: string;
        area_id: string;
        agent_id: string;
        status_id: string;
        days: number[] | undefined;
      }
    >({
      transformResponse: (response: { data: INotYetOrdered[] }) =>
        response.data,
      query: ({
        search,
        from_date,
        to_date,
        area_id,
        agent_id,
        status_id,
        days,
      }) => ({
        url: '/orders/not-yet-ordered',
        method: 'GET',
        params: {
          search,
          from_date,
          to_date,
          area_id,
          agent_id,
          status_id,
          days: JSON.stringify(days),
        },
      }),
    }),
    orderReminder: build.mutation<any, number>({
      query: (id) => ({
        url: `/clients/${id}/order-reminder`,
        method: 'POST',
      }),
    }),
    returnOrderList: build.query<
      IReturnOrderListResponse,
      {
        search: string;
        from_date: string;
        to_date: string;
        area_id: string;
        page: number;
      }
    >({
      transformResponse: (response: { data: IReturnOrderListResponse }) =>
        response.data,
      query: ({ search, from_date, to_date, area_id, page }) => ({
        url: '/returns',
        method: 'GET',
        params: { search, from_date, to_date, area_id, page },
      }),
    }),
    confirmFreeShipping: build.mutation<
      any,
      { orderId: string; is_free_shipping: number }
    >({
      query: ({ is_free_shipping, orderId }) => ({
        url: `/orders/${orderId}/free-shipping`,
        method: 'POST',
        body: {
          is_free_shipping,
        },
      }),
    }),
    acceptPaidOrder: build.mutation<any, { orderId: string }>({
      query: ({ orderId }) => ({
        url: `/orders/${orderId}/paid`,
        method: 'POST',
      }),
    }),
    getReturnItem: build.query<ReturnItem, string>({
      transformResponse: (response: { data: ReturnItem }) => response.data,
      query: (return_id) => ({
        url: `/returns/${return_id}`,
        method: 'GET',
      }),
    }),
    specialOffersOrders: build.query<ISpecialOffersOrders[], string>({
      transformResponse: (response: { data: ISpecialOffersOrders[] }) =>
        response.data,
      query: (business_id) => ({
        url: '/orders/special-offers',
        method: 'GET',
        params: { business_id },
      }),
    }),
  }),
});
